import { Injectable, Inject } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import { DOCUMENT } from '@angular/common';

@Injectable({
  providedIn: 'root',
})
export class SeoService {
  private jsonLdElement: HTMLScriptElement | null = null;

  constructor(
    private metaTagService: Meta,
    private titleService: Title,
    @Inject(DOCUMENT) private document: Document
  ) {}

  // Renders a schema.org JSON-LD <script> into <head> - works under SSR the
  // same way setCanonicalURL() does, since Angular Universal serializes
  // whatever's in document.head at render time. Removes any script this
  // service previously added so navigating between pages (e.g. one blog
  // post to another) doesn't accumulate stale/duplicate JSON-LD blocks.
  setJsonLd(data: object): void {
    if (this.jsonLdElement) {
      this.jsonLdElement.remove();
      this.jsonLdElement = null;
    }

    const script = this.document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(data);
    this.document.head.appendChild(script);
    this.jsonLdElement = script;
  }

  clearJsonLd(): void {
    if (this.jsonLdElement) {
      this.jsonLdElement.remove();
      this.jsonLdElement = null;
    }
  }

  generateTags(config) {
    // default values
    config = {
      title: 'Myexclusive Villas. Luxury Holiday Rentals',
      description: 'Myexclusive villas - book your villa holiday or apartment.',
      image: 'https://myexclusivevillas.com/assets/img/mxvLogo.svg',
      slug: '',
      ...config,
    };

    this.metaTagService.updateTag({ name: 'twitter:card', content: 'summary' });
    this.metaTagService.updateTag({
      name: 'twitter:site',
      content: '@myexclusivev',
    });
    this.metaTagService.updateTag({
      name: 'twitter:title',
      content: config.title,
    });
    this.metaTagService.updateTag({
      name: 'twitter:description',
      content: config.description,
    });
    this.metaTagService.updateTag({
      name: 'twitter:image',
      content: config.image,
    });

    this.metaTagService.updateTag({ property: 'og:type', content: 'website' });
    this.metaTagService.updateTag({
      property: 'og:site_name',
      content: 'Myexclusive Villas',
    });
    this.metaTagService.updateTag({
      property: 'og:title',
      content: config.title,
    });
    this.metaTagService.updateTag({
      property: 'og:description',
      content: config.description,
    });
    this.metaTagService.updateTag({
      property: 'og:image',
      content: config.image,
    });
    this.metaTagService.updateTag({
      property: 'og:url',
      content: `${this.getCanonicalURL()}`,
    });
  }

  setTitle(title) {
    this.titleService.setTitle(title);
  }

  updateTag(metaTag) {
    this.metaTagService.updateTag(metaTag);
  }

  setCanonicalURL(url?: string) {
    const canURL = this.getCanonicalURL(url);
    const link: HTMLLinkElement = this.document.createElement('link');
    link.setAttribute('rel', 'canonical');
    this.document.head.appendChild(link);
    link.setAttribute('href', canURL);
  }

  getCanonicalURL(url?: string) {
    return url === undefined ? this.document.URL : url;
  }
}
